import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  WbSunny,
  Cloud,
  Thunderstorm,
  AcUnit,
  Grain,
  WaterDrop,
  AccessTime,
  CurrencyExchange,
} from '@mui/icons-material';
import { useRates } from '../../context/RatesContext';

const srOnly = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
};

// OpenWeather API ключ
const OPENWEATHER_API_KEY = 'b18e470ac2546ff8f412454b312e053b';
const BISHKEK_CITY_ID = 1528675; // Бишкек

/**
 * HeaderInfoBar - информационная панель в шапке
 * Показывает: курсы валют НБКР, текущее время, погоду в Бишкеке
 */
const HeaderInfoBar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const { rates, loading: ratesLoading, asOfDate } = useRates();

  const fmt = useCallback(
    (val) =>
      val !== undefined && val !== null
        ? new Intl.NumberFormat('ru-RU', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(val)
        : '—',
    []
  );

  const [currentTime, setCurrentTime] = useState(new Date());
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [weatherError, setWeatherError] = useState(null);
  const [offline, setOffline] = useState(!navigator?.onLine);

  // Обновление времени каждую секунду
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    const handleOnline = () => setOffline(false);
    const handleOffline = () => setOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => clearInterval(timer);
  }, []);

  // Загрузка погоды
  const fetchWeather = useCallback(async () => {
    try {
      setWeatherLoading(true);
      const response = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?id=${BISHKEK_CITY_ID}&appid=${OPENWEATHER_API_KEY}&units=metric&lang=ru`
      );
      if (!response.ok) throw new Error('Weather API error');
      const data = await response.json();
      setWeather({
        temp: Math.round(data.main.temp),
        feels_like: Math.round(data.main.feels_like),
        description: data.weather[0].description,
        icon: data.weather[0].icon,
        main: data.weather[0].main,
        humidity: data.main.humidity,
        wind: Math.round(data.wind.speed),
      });
      setWeatherError(null);
    } catch (err) {
      console.error('Weather fetch error:', err);
      setWeatherError('Не удалось загрузить погоду');
    } finally {
      setWeatherLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWeather();
    // Обновляем погоду каждые 10 минут
    const weatherTimer = setInterval(fetchWeather, 10 * 60 * 1000);
    return () => clearInterval(weatherTimer);
  }, [fetchWeather]);

  // Иконка погоды
  const getWeatherIcon = (main) => {
    switch (main?.toLowerCase()) {
      case 'clear':
        return <WbSunny sx={{ fontSize: 18, color: '#ffc107' }} />;
      case 'clouds':
        return <Cloud sx={{ fontSize: 18, color: '#90a4ae' }} />;
      case 'rain':
      case 'drizzle':
        return <WaterDrop sx={{ fontSize: 18, color: '#2196f3' }} />;
      case 'thunderstorm':
        return <Thunderstorm sx={{ fontSize: 18, color: '#7c4dff' }} />;
      case 'snow':
        return <AcUnit sx={{ fontSize: 18, color: '#80deea' }} />;
      case 'mist':
      case 'fog':
      case 'haze':
        return <Grain sx={{ fontSize: 18, color: '#b0bec5' }} />;
      default:
        return <WbSunny sx={{ fontSize: 18 }} />;
    }
  };

  // Форматирование времени (Бишкек UTC+6)
  const formatTime = (date) => {
    return date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Asia/Bishkek',
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('ru-RU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      timeZone: 'Asia/Bishkek',
    });
  };

  // Если мобильный - показываем только время и погоду в компактном виде
  if (isMobile) {
    return (
      <Box
        sx={{
          bgcolor: 'primary.dark',
          color: 'white',
          py: 0.5,
          px: 2,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          rowGap: 0.5,
          columnGap: 1,
          fontSize: '0.75rem',
        }}
        aria-live="polite"
      >
        {/* Время */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AccessTime sx={{ fontSize: 14 }} />
          <Typography variant="caption">{formatTime(currentTime)}</Typography>
        </Box>

        {/* Погода */}
        {weatherLoading ? (
          <Skeleton variant="text" width={40} height={16} sx={{ bgcolor: 'grey.700' }} />
        ) : weather ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, position: 'relative' }}>
            {getWeatherIcon(weather.main)}
            <Typography variant="caption">{weather.temp}°C</Typography>
            <span style={srOnly}>
              Погода: {weather.description}, ощущается как {weather.feels_like} градусов
            </span>
          </Box>
        ) : (
          <Typography variant="caption" color="grey.400">
            —°C{offline ? ' · оффлайн' : ''}
          </Typography>
        )}

        {/* Курс USD */}
        {ratesLoading ? (
          <Skeleton variant="text" width={50} height={16} sx={{ bgcolor: 'grey.700' }} />
        ) : rates?.USD ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption">$ {fmt(rates.USD)}</Typography>
          </Box>
        ) : (
          <Typography variant="caption" color="grey.400">
            —
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'grey.900',
        color: 'grey.300',
        py: 0.75,
        borderBottom: '1px solid',
        borderColor: 'grey.800',
      }}
      aria-live="polite"
    >
      <Box
        sx={{
          maxWidth: 'xl',
          mx: 'auto',
          px: { xs: 2, md: 3 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {/* Левая часть - курсы валют */}
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Tooltip title={asOfDate ? `Обновлено: ${asOfDate}` : 'Курсы НБКР'}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CurrencyExchange sx={{ fontSize: 16, color: 'primary.light' }} />
              <Typography
                variant="caption"
                sx={{ fontWeight: 500, color: 'grey.400' }}
              >
                {asOfDate ? `НБКР · ${asOfDate}` : 'НБКР'}
              </Typography>
            </Box>
          </Tooltip>

          {ratesLoading ? (
            <Skeleton
              variant="rectangular"
              width={isTablet ? 160 : 200}
              height={26}
              sx={{ bgcolor: 'grey.800', borderRadius: 1 }}
            />
          ) : rates ? (
            <Box
              sx={{
                display: 'flex',
                gap: isTablet ? 1 : 1.5,
                flexWrap: 'wrap',
                rowGap: 1,
                maxWidth: { xs: '100%', md: 480 },
              }}
            >
              <Tooltip title="Доллар США">
                <Chip
                  size="small"
                  label={`$ ${fmt(rates.USD)}`}
                  sx={{
                    bgcolor: 'transparent',
                    border: '1px solid',
                    borderColor: 'grey.600',
                    color: 'grey.100',
                    height: 24,
                    '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
                  }}
                />
              </Tooltip>
              <Tooltip title="Российский рубль">
                <Chip
                  size="small"
                  label={`₽ ${fmt(rates.RUB)}`}
                  sx={{
                    bgcolor: 'transparent',
                    border: '1px solid',
                    borderColor: 'grey.600',
                    color: 'grey.100',
                    height: 24,
                    '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
                  }}
                />
              </Tooltip>
              {!isTablet && (
                <Tooltip title="Евро">
                  <Chip
                    size="small"
                    label={`€ ${fmt(rates.EUR)}`}
                    sx={{
                      bgcolor: 'transparent',
                      border: '1px solid',
                      borderColor: 'grey.600',
                      color: 'grey.100',
                      height: 24,
                      '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          ) : (
            <Typography variant="caption" color="grey.500">
              Недоступно
            </Typography>
          )}
        </Box>

        {/* Центр - погода */}
        <Tooltip
          title={
            weather
              ? `${weather.description}, влажность ${weather.humidity}%, ветер ${weather.wind} м/с`
              : 'Погода в Бишкеке'
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {weatherLoading ? (
              <Skeleton
                variant="rectangular"
                width={120}
                height={24}
                sx={{ bgcolor: 'grey.800', borderRadius: 1 }}
              />
            ) : weather ? (
              <>
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getWeatherIcon(weather.main)}
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    Бишкек: {weather.temp}°C
                  </Typography>
                  <Typography
                    variant="caption"
                    color="grey.500"
                    sx={{ display: { xs: 'none', lg: 'inline' } }}
                  >
                    ({weather.description})
                  </Typography>
                  <span style={srOnly}>
                    Погода: {weather.description}, ощущается как {weather.feels_like} градусов, влажность {weather.humidity}%, ветер {weather.wind} м/с
                  </span>
                </Box>
              </>
            ) : weatherError ? (
              <Typography variant="caption" color="grey.500">
                Погода недоступна{offline ? ' · оффлайн' : ''}
              </Typography>
            ) : (
              <Typography variant="caption" color="grey.500">
                —°C{offline ? ' · оффлайн' : ''}
              </Typography>
            )}
          </Box>
        </Tooltip>

        {/* Правая часть - дата и время */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="caption" color="grey.500">
            {formatDate(currentTime)}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: 'grey.800',
              px: 1,
              py: 0.25,
              borderRadius: 1,
            }}
          >
            <AccessTime sx={{ fontSize: 14, color: 'primary.light' }} />
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 600,
                color: 'grey.100',
                minWidth: 60,
              }}
            >
              {formatTime(currentTime)}
            </Typography>
          </Box>
            {offline && (
              <Typography variant="caption" color="warning.main">
                оффлайн
              </Typography>
            )}
        </Box>
      </Box>
    </Box>
  );
};

export default HeaderInfoBar;
